import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SEO, getOrganizationSchema, getPersonSchema } from '../components/SEO';
import { SimplePracticeButton } from '../components/SimplePracticeButton';
import { 
  Users, Heart, Baby, Brain, Shield, Home, 
  Phone, Star, CheckCircle, Award, Clock, MapPin 
} from 'lucide-react';

// Data constants
const therapistInfo = {
  name: "Keith Scherdt, LPC, LMHC",
  phone: "(808) 470-7650",
  email: "keith@mhcmaui.com",
  address: "2200 Main Street",
  city: "Wailuku",
  state: "HI",
  zip: "96793",
  location: "Wailuku, HI",
  credentials: "Licensed Professional Counselor | Licensed Mental Health Counselor",
  image: "https://customer-assets.emergentagent.com/job_aloha-parents/artifacts/we1ubgtn_file_00000000f90471f5a3205e77abce95f6.png",
  bio: "With over a decade of experience in mental health counseling, I specialize in helping parents and families navigate the unique challenges of raising children in today's world. My approach combines evidence-based techniques with compassionate, culturally-sensitive care tailored to Hawaii's diverse community."
};

const services = [
  {
    id: 1,
    name: "Parenting Support",
    icon: "Users",
    color: "teal",
    description: "Expert guidance for navigating the challenges of modern parenting",
    link: "/services/parenting-support"
  },
  {
    id: 2,
    name: "Family Therapy",
    icon: "Heart",
    color: "pink",
    description: "Strengthening family bonds and improving communication",
    link: "/services/family-therapy"
  },
  {
    id: 3,
    name: "Child & Adolescent Counseling",
    icon: "Baby",
    color: "blue",
    description: "Specialized support for children and teenagers",
    link: "/services/child-adolescent"
  }
];

const specializations = [
  "Parenting & Child Development",
  "Family Conflict Resolution",
  "Anxiety & Depression",
  "Adoption & Foster Care Support",
  "Behavioral Issues",
  "Trauma & PTSD"
];

const approaches = [
  "Cognitive Behavioral Therapy (CBT)",
  "Family Systems Therapy",
  "Attachment-Based Therapy",
  "Trauma-Informed Care",
  "Solution-Focused Brief Therapy"
];

const testimonials = [
  {
    id: 1,
    name: "Sarah M.",
    location: "Wailuku, HI",
    rating: 5,
    text: "Keith has been instrumental in helping our family navigate some challenging times. His compassionate approach and practical strategies have made a real difference."
  },
  {
    id: 2,
    name: "Michael K.",
    location: "Kahului, HI",
    rating: 5,
    text: "As a first-time parent, I was struggling with anxiety. Keith's parenting support sessions gave me the confidence and tools I needed to be the parent I want to be."
  },
  {
    id: 3,
    name: "Jennifer L.",
    location: "Kihei, HI",
    rating: 5,
    text: "Our family therapy sessions helped us reconnect and communicate better. Keith creates a safe, supportive environment where everyone feels heard."
  }
];

const blogPosts = [
  {
    id: 1,
    title: "Understanding Your Child's Emotional Development",
    excerpt: "Learn about the key stages of emotional growth and how to support your child through each phase.",
    category: "Parenting Tips",
    readTime: "5 min",
    date: "Dec 15, 2024",
    image: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800&q=80"
  },
  {
    id: 2,
    title: "Managing Anxiety in Adolescents",
    excerpt: "Practical strategies to help teenagers cope with stress and anxiety in today's challenging world.",
    category: "Adolescent Care",
    readTime: "7 min",
    date: "Dec 10, 2024",
    image: "https://images.unsplash.com/photo-1509099836639-18ba1795216d?w=800&q=80"
  },
  {
    id: 3,
    title: "Building Strong Family Connections",
    excerpt: "Simple daily practices that strengthen family bonds and improve communication.",
    category: "Family Therapy",
    readTime: "4 min",
    date: "Dec 5, 2024",
    image: "https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800&q=80"
  },
  {
    id: 4,
    title: "Recognizing Signs of Depression in Children",
    excerpt: "Important behavioral changes parents should watch for and when to seek professional help.",
    category: "Mental Health",
    readTime: "6 min",
    date: "Dec 1, 2024",
    image: "https://images.unsplash.com/photo-1517164850305-99a3e65bb47e?w=800&q=80"
  }
];

export const HomePage = () => {
  const navigate = useNavigate();

  const iconMap = {
    Users, Heart, Baby, Brain, Shield, Home
  };

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [getOrganizationSchema(), getPersonSchema()]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Mindful Health Counseling - Parenting & Family Therapy in Hawaii"
        description="Licensed mental health counselor in Wailuku, HI specializing in parenting support, family therapy, child & adolescent counseling. Telehealth available throughout Hawaii."
        keywords="mental health counselor Hawaii, family therapist Wailuku, parenting support Maui, child therapist Hawaii, LMHC Hawaii"
        canonicalUrl={process.env.REACT_APP_BACKEND_URL || ''}
        schemaData={combinedSchema}
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 via-blue-50 to-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzE0Yjg5OCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNSIvPjwvZz48L3N2Zz4=')] opacity-40"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-teal-100 text-teal-800 hover:bg-teal-200">
                Hawaii's Trusted Family Therapist
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Helping Families
                <span className="block text-teal-600">Thrive Together</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Expert parenting support and family therapy in Hawaii. Build stronger 
                relationships, overcome challenges, and create the peaceful home you deserve.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <SimplePracticeButton variant="appointment" size="lg" />
                <SimplePracticeButton variant="consultation" size="lg" />
              </div>
              <div className="mt-8 flex items-center space-x-6">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-teal-600 mr-2" />
                  <span className="text-sm text-gray-600">Telehealth Available</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-teal-600 mr-2" />
                  <span className="text-sm text-gray-600">Accepting New Clients</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800&q=80" 
                  alt="Happy family"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-900/20 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center space-x-3">
                  <Award className="w-10 h-10 text-teal-600" />
                  <div>
                    <p className="font-bold text-gray-900">Licensed & Certified</p>
                    <p className="text-sm text-gray-600">LPC, LMHC</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info Bar */}
      <section className="bg-teal-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex items-center justify-center space-x-3">
              <Phone className="w-6 h-6" />
              <div className="text-left">
                <p className="text-sm opacity-90">Call Us Today</p>
                <a href="tel:8084707650" className="font-bold text-lg hover:underline">
                  {therapistInfo.phone}
                </a>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <MapPin className="w-6 h-6" />
              <div className="text-left">
                <p className="text-sm opacity-90">Located In</p>
                <p className="font-bold text-lg">{therapistInfo.location}</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <Clock className="w-6 h-6" />
              <div className="text-left">
                <p className="text-sm opacity-90">Session Fee</p>
                <p className="font-bold text-lg">$115 / 50 min</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <Badge className="mb-4 bg-blue-100 text-blue-800">About The Therapist</Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Meet {therapistInfo.name}
              </h2>
              <p className="text-lg text-gray-600 mb-4">{therapistInfo.credentials}</p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {therapistInfo.bio}
              </p>
              <div className="space-y-3 mb-8">
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-gray-700">Licensed Professional Counselor (LPC)</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-gray-700">Licensed Mental Health Counselor (LMHC)</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-gray-700">Over 11 years experience in education</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/contact')}
                className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg transition-all duration-200"
              >
                Schedule Your Appointment
              </Button>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute inset-0 bg-teal-200 rounded-2xl transform rotate-3"></div>
                <img 
                  src={therapistInfo.image}
                  alt={therapistInfo.name}
                  className="relative rounded-2xl shadow-xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-purple-100 text-purple-800">Our Services</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Mental Health Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized therapy services designed to help families navigate challenges 
              and build stronger, healthier relationships.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card 
                  key={service.id} 
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white cursor-pointer"
                  onClick={() => navigate(service.link)}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="w-7 h-7 text-teal-600" />
                    </div>
                    <CardTitle className="text-xl">{service.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 text-base mb-4">
                      {service.description}
                    </CardDescription>
                    <Button variant="link" className="text-teal-600 hover:text-teal-700 p-0 h-auto font-semibold">
                      Learn More →
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="border-none shadow-lg bg-white">
              <CardHeader>
                <CardTitle className="text-2xl">Areas of Specialization</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {specializations.map((spec, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary"
                      className="bg-teal-100 text-teal-800 px-3 py-1 text-sm"
                    >
                      {spec}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white">
              <CardHeader>
                <CardTitle className="text-2xl">Therapeutic Approaches</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {approaches.map((approach, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{approach}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-amber-100 text-amber-800">Client Testimonials</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Families Are Saying
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from families who have found healing and growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-none shadow-lg bg-gradient-to-br from-white to-teal-50">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div>
              <Badge className="mb-4 bg-green-100 text-green-800">Parenting Resources</Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Latest Insights & Tips
              </h2>
              <p className="text-xl text-gray-600">
                Evidence-based advice for modern parents
              </p>
            </div>
            <Button 
              variant="outline"
              onClick={() => navigate('/resources')}
              className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 hidden md:block transition-all duration-200"
            >
              View All Articles
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {blogPosts.slice(0, 4).map((post) => (
              <Card 
                key={post.id} 
                className="border-none shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer bg-white overflow-hidden group"
                onClick={() => navigate('/resources')}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <Badge className="absolute top-4 left-4 bg-white text-gray-800">
                    {post.category}
                  </Badge>
                </div>
                <CardHeader>
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <span>{post.date}</span>
                    <span className="mx-2">•</span>
                    <span>{post.readTime}</span>
                  </div>
                  <CardTitle className="text-xl group-hover:text-teal-600 transition-colors">
                    {post.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{post.excerpt}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8 md:hidden">
            <Button 
              variant="outline"
              onClick={() => navigate('/resources')}
              className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 transition-all duration-200"
            >
              View All Articles
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-600 to-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl mb-8 opacity-95">
            Take the first step toward a healthier, happier family. 
            Schedule your consultation today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold transition-all duration-200 shadow-xl hover:shadow-2xl"
            >
              Book Your Appointment
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => window.location.href = `tel:${therapistInfo.phone.replace(/[^0-9]/g, '')}`}
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold transition-all duration-200"
            >
              Call {therapistInfo.phone}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
